import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { cellClassName } from './columns';
import type { BookColumn, CellContext } from './columns';
import { GROUP_BOOKED, nextPreference } from './boardPref';
import type { BoardPref } from './boardPref';
import { ACCOUNT_FLOOR_PX, PREF_COLUMN_PX, columnsThatFit, tableMinWidth } from './columnFit';
import type { RankedAccount } from './derived';
import type { SortState } from './sort';

// Show which column is driving the order and which way, so a sorted table never looks like the
// default one -- and say the same thing to a screen reader, which otherwise has no way to know.
//
// `aria-sort="none"` is correct for an unsorted column, but "worklist order" is not "unsorted"
// to anyone reading this page -- it is the deliberate default. The title says which of the two
// you are actually in.
export function SortableHeader({
  column,
  sort,
  onSort,
}: {
  column: BookColumn;
  sort: SortState;
  onSort: (key: string) => void;
}) {
  const isActive = sort.key === column.key;
  const ariaSort = !isActive ? 'none' : sort.direction === -1 ? 'descending' : 'ascending';

  return (
    <th
      className={[
        column.numeric ? 'num' : '',
        isActive ? 'on' : '',
        column.key === 'account' ? 'acctname' : '',
      ].filter(Boolean).join(' ')}
      aria-sort={ariaSort}
      role="columnheader"
      tabIndex={0}
      data-col={column.key}
      style={column.minWidth ? { minWidth: column.minWidth } : undefined}
      // The sort instruction, prefixed by what the column *means* where it has a hint. Four
      // of these columns are derived readings rather than stored fields, and "Reachable"
      // especially looks like a plain count of contacts and is not one.
      title={
        (column.hint ? column.hint + ' ' : '')
        + (isActive
          ? `Sorted ${sort.direction === -1 ? 'high to low' : 'low to high'}. `
            + 'Activate to reverse, again to return to worklist order.'
          : 'Activate to sort by this column. Currently in worklist order.')
      }
      onClick={() => onSort(column.key)}
      // Enter and Space, the two keys a control of this shape is expected to answer. Space is
      // prevented first or it scrolls the page instead of sorting.
      onKeyDown={event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onSort(column.key);
      }}
    >
      {column.label}
      {isActive ? <span className="sortarrow">{sort.direction === -1 ? '▼' : '▲'}</span> : null}
    </th>
  );
}

function PrefButtons({
  row,
  canEdit,
  pending,
  onSetPref,
}: {
  row: RankedAccount;
  canEdit: boolean;
  pending: boolean;
  onSetPref: (accountId: string, preference: BoardPref) => void;
}) {
  const pref = row.board_pref ?? null;
  return (
    <div className="prefbtns">
      <button
        type="button"
        className="prefbtn"
        aria-label={`Pin ${row.account}`}
        aria-pressed={pref === 'pinned'}
        disabled={!canEdit || pending}
        onClick={() => onSetPref(row.id, nextPreference(pref, 'pinned'))}
      >
        Pin
      </button>
      <button
        type="button"
        className="prefbtn"
        aria-label={`Hide ${row.account}`}
        aria-pressed={pref === 'hidden'}
        disabled={!canEdit || pending}
        onClick={() => onSetPref(row.id, nextPreference(pref, 'hidden'))}
      >
        Hide
      </button>
    </div>
  );
}

export function rowClassName(row: RankedAccount, isOpen: boolean): string {
  const classes = ['acct'];
  if (row.touches === 0) classes.push('untouched');
  if (isOpen) classes.push('open');
  if (row.board_pref === 'pinned') classes.push('pref-pinned');
  if (row.board_pref === 'hidden') classes.push('pref-hidden');
  if (row.group === GROUP_BOOKED) classes.push('meeting-booked');
  return classes.join(' ');
}

function BookRow({
  row,
  columns,
  context,
  isOpen,
  onToggle,
  canEditPrefs,
  pendingPref,
  onSetPref,
}: {
  row: RankedAccount;
  columns: BookColumn[];
  context: CellContext;
  isOpen: boolean;
  onToggle: () => void;
  canEditPrefs: boolean;
  pendingPref: boolean;
  onSetPref: (accountId: string, preference: BoardPref) => void;
}) {
  return (
    <tr
      className={rowClassName(row, isOpen)}
      tabIndex={0}
      role="button"
      aria-expanded={isOpen}
      aria-label={`${row.account} — open company details`}
      // A link or a button inside the row (the company site, a Request) is its own
      // action. The same exemption is needed on the keyboard path, or Enter on a
      // focused control would fire it and toggle the sidebar at once.
      onClick={event => {
        if ((event.target as HTMLElement).closest('a, button')) return;
        onToggle();
      }}
      onKeyDown={event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if ((event.target as HTMLElement).closest('a, button')) return;
        event.preventDefault();
        onToggle();
      }}
    >
      {columns.map(column => (
        <td
          key={column.key}
          className={cellClassName(column, row)}
          data-col={column.key}
          style={column.minWidth ? { minWidth: column.minWidth } : undefined}
        >
          {column.cell(row, context)}
        </td>
      ))}
      {/* Own column with a reserved width. GRO-139: the account name is the slack
          absorber and the first thing a sidebar squeeze collapses — do not put
          these controls there. */}
      <td className="prefcell">
        <PrefButtons
          row={row}
          canEdit={canEditPrefs}
          pending={pendingPref}
          onSetPref={onSetPref}
        />
      </td>
    </tr>
  );
}

export function BookTable({
  rows,
  columns,
  context,
  sort,
  onSort,
  openAccountId,
  onToggleAccount,
  canEditPrefs,
  pendingPrefId,
  onSetPref,
}: {
  rows: RankedAccount[];
  columns: BookColumn[];
  context: CellContext;
  sort: SortState;
  onSort: (key: string) => void;
  openAccountId: string | null;
  onToggleAccount: (accountId: string) => void;
  canEditPrefs: boolean;
  pendingPrefId: string | null;
  onSetPref: (accountId: string, preference: BoardPref) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [wrapWidth, setWrapWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const node = wrapRef.current;
    if (!node) return;
    const update = () => setWrapWidth(node.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Drop against the wrap, not the viewport. An extension panel narrows the page without
  // a matchMedia breakpoint firing, which is how the name vanished in GRO-139.
  // The Pin / Hide column is always drawn, so the data columns fit into what it leaves.
  const visible = wrapWidth == null ? columns : columnsThatFit(columns, wrapWidth - PREF_COLUMN_PX);

  return (
    <div
      ref={wrapRef}
      className="tablewrap"
      style={{ marginTop: 0 }}
      tabIndex={0}
      role="region"
      aria-label="Accounts"
    >
      <table
        className="booktable"
        style={{
          minWidth: tableMinWidth(visible) + PREF_COLUMN_PX,
          '--book-account-floor': ACCOUNT_FLOOR_PX + 'px',
        } as CSSProperties}
      >
        {/* Widths come from the column array, so the table is laid out by what each column is
            for rather than by whichever row holds the longest string. Paired with
            `table-layout: fixed` in table.css -- without that a `<col>` width is a suggestion
            the content overrides, which is how Signal grew to 669px and pushed three columns
            off the right edge. The `<col>` elements stay; columnFit.ts only omits a column
            entirely, it does not let content re-size one. */}
        <colgroup>
          {visible.map(column => (
            <col
              key={column.key}
              data-col={column.key}
              style={{
                width: column.width,
                minWidth: column.minWidth,
              }}
            />
          ))}
          <col className="prefcol" />
        </colgroup>
        <thead>
          <tr>
            {visible.map(column => (
              <SortableHeader key={column.key} column={column} sort={sort} onSort={onSort} />
            ))}
            <th className="prefhead" scope="col">
              <span className="visually-hidden">Pin or hide</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <BookRow
              key={row.id}
              row={row}
              columns={visible}
              context={context}
              isOpen={openAccountId === row.id}
              onToggle={() => onToggleAccount(row.id)}
              canEditPrefs={canEditPrefs}
              pendingPref={pendingPrefId === row.id}
              onSetPref={onSetPref}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
